import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { VENUES, type Venue } from "@/lib/venues-data";
import { SITE, CITIES } from "@/lib/site-data";
import { CTABand } from "@/components/CTA";
import { MapPin, Building2, Car, Plane, Calendar, ExternalLink, Trophy } from "lucide-react";
import fleetHero from "@/assets/fleet-hero.jpg";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ServiceStandards } from "@/components/ServiceStandards";
import { HourlyRates } from "@/components/HourlyRates";
import { InstantPriceWidget } from "@/components/InstantPriceWidget";
import { Testimonials } from "@/components/Testimonials";
import { pricingFaqJsonLd } from "@/lib/pricing-faq";
import { ldJson, imageObjectJsonLd } from "@/lib/seo-schema";

const mapsUrl = (q: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const SITE_URL = "https://premierexecutivechauffeur.com";

export const Route = createFileRoute("/venues/$slug")({
  loader: ({ params }) => {
    const venue = VENUES.find((v) => v.slug === params.slug);
    if (!venue) throw notFound();
    return { venue };
  },
  head: ({ params, loaderData }) => {
    const venue = loaderData?.venue;
    if (!venue) return { meta: [{ title: "Venue" }] };

    // Title 50–60 chars
    const titleLong = `${venue.shortName} ${venue.h1Suffix} | ${venue.city}, CA`;
    const titleShort = `${venue.shortName} ${venue.h1Suffix}`;
    const title = titleLong.length <= 62 ? titleLong : titleShort;

    // Description ≤160
    const base = `${venue.shortName} chauffeur service — sedan, SUV & Sprinter. SFO in ${venue.drive.sfo}, SJC in ${venue.drive.sjc}.`;
    const tail = ` Pre-arranged, flight-tracked, CPUC-licensed & insured.`;
    const description = (base + tail).length <= 160 ? base + tail : base + " CPUC-licensed.";

    const canonical = `/venues/${params.slug}`;
    const absCanonical = `${SITE_URL}${canonical}`;
    const ogImage = `${SITE_URL}${venue.hero}`;
    const ogImageAlt = `${venue.name} in ${venue.city}, California — black car service & chauffeur by Premier Executive Chauffeur (Lincoln Aviator midsize SUV (sedan-class service), Chevrolet Suburban SUV, Mercedes-Benz Sprinter)`;

    // Place / LocalBusiness / Service schema stack
    const place = {
      "@context": "https://schema.org",
      "@type": venue.category === "Stadium" ? "StadiumOrArena"
             : venue.category === "Arena" ? "StadiumOrArena"
             : venue.category === "Convention Center" ? "ConventionCenter"
             : "Place",
      "@id": `${absCanonical}#place`,
      name: venue.name,
      address: {
        "@type": "PostalAddress",
        streetAddress: venue.addressStreet,
        addressLocality: venue.addressLocality,
        addressRegion: venue.addressRegion,
        postalCode: venue.postalCode,
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: venue.lat, longitude: venue.lng },
      url: absCanonical,
      image: imageObjectJsonLd({ url: ogImage, contentUrl: ogImage, caption: ogImageAlt, description: ogImageAlt }),
    };

    const service = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${absCanonical}#service`,
      serviceType: `${venue.shortName} Chauffeur & Black Car Service`,
      name: `${venue.shortName} ${venue.h1Suffix}`,
      description,
      provider: { "@id": `${SITE_URL}#business` },
      areaServed: { "@type": "Place", name: venue.name },
      audience: {
        "@type": "BusinessAudience",
        audienceType: venue.category === "Corporate Campus" ? "Corporate Executives, Board Members, Candidates"
                    : venue.category === "Convention Center" ? "Conference Attendees, Speakers, Corporate Groups"
                    : "Fans, Concert-goers, Corporate Suite Guests",
      },
      offers: {
        "@type": "Offer",
        url: SITE.bookUrl,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    };

    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${absCanonical}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Venues", item: `${SITE_URL}/venues` },
        { "@type": "ListItem", position: 3, name: venue.shortName, item: absCanonical },
      ],
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: venue.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    const webPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${absCanonical}#webpage`,
      url: absCanonical,
      name: `${venue.shortName} ${venue.h1Suffix}`,
      description,
      inLanguage: "en-US",
      isPartOf: { "@id": `${SITE_URL}#website` },
      about: { "@id": `${absCanonical}#place` },
      breadcrumb: { "@id": `${absCanonical}#breadcrumb` },
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2"] },
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: venue.keywords.join(", ") },
        { name: "geo.region", content: "US-CA" },
        { name: "geo.placename", content: `${venue.city}, California` },
        { name: "geo.position", content: `${venue.lat};${venue.lng}` },
        { name: "ICBM", content: `${venue.lat}, ${venue.lng}` },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: absCanonical },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: ogImageAlt },
        { property: "og:image:width", content: "1216" },
        { property: "og:image:height", content: "640" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: ogImageAlt },
      ],
      links: [{ rel: "canonical", href: absCanonical }],
      scripts: [
        { type: "application/ld+json", children: ldJson(place) },
        { type: "application/ld+json", children: ldJson(service) },
        { type: "application/ld+json", children: ldJson(breadcrumbs) },
        { type: "application/ld+json", children: ldJson(faqSchema) },
        { type: "application/ld+json", children: ldJson(webPage) },
        { type: "application/ld+json", children: ldJson(pricingFaqJsonLd(absCanonical)) },
      ],
    };
  },
  component: VenuePage,
  notFoundComponent: () => (
    <div className="container-x py-24 text-center">
      <h1 className="text-3xl mb-4">Venue not found</h1>
      <Link to="/venues" className="text-gold underline underline-offset-2">View all venues & campuses</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-x py-24 text-center">
      <h1 className="text-3xl mb-4">Something went wrong</h1>
      <p className="text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function VenuePage() {
  const data = Route.useLoaderData();
  const venue = data.venue as Venue;
  const cityData = CITIES.find((c) => c.slug === venue.cityLink);
  const related = venue.related
    .map((slug: string) => VENUES.find((x) => x.slug === slug))
    .filter((v): v is Venue => Boolean(v));

  const heroAlt = `${venue.name} in ${venue.city}, California — car service & chauffeur by Premier Executive Chauffeur`;
  const fleetAlt = `Black Lincoln Aviator, Chevrolet Suburban LT SUV, and Mercedes-Benz Sprinter passenger van chauffeur fleet for ${venue.name} — pre-arranged airport transfers, event drop-off, and group transportation`;

  return (
    <>
      <section className="container-x py-20 md:py-28 max-w-4xl">
        <Breadcrumbs
          items={[{ name: "Home", to: "/" }, { name: "Venues", to: "/venues" }, { name: venue.shortName }]}
          className="mb-4"
        />
        <figure className="mb-8 border border-border overflow-hidden bg-surface">
          <img
            src={venue.hero}
            alt={heroAlt}
            width={1216}
            height={640}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-auto block"
          />
          <figcaption className="sr-only">{heroAlt}</figcaption>
        </figure>
        <h1 className="text-4xl md:text-6xl mb-6">
          {venue.name} {venue.h1Suffix}
        </h1>
        <p className="eyebrow mb-4 text-gold-soft">
          {venue.city}, CA · SFO in {venue.drive.sfo} · SJC in {venue.drive.sjc} · CPUC-licensed
        </p>
        <p className="text-lg text-muted-foreground speakable">{venue.intro}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href={SITE.bookUrl} target="_blank" rel="noopener noreferrer" className="btn-gold hover:btn-gold-hover">
            Instant Quote / Reserve
          </a>
          <a href={SITE.phoneHref} className="btn-outline hover:bg-gold hover:text-primary-foreground">
            Call {SITE.phone}
          </a>
        </div>
      </section>

      {/* AT-A-GLANCE STRIP */}
      <section className="border-t border-border bg-surface">
        <div className="container-x py-10">
          <div className="grid gap-4 md:grid-cols-4 text-sm">
            <div className="border border-border bg-background p-5">
              <div className="flex items-center gap-2 mb-1"><MapPin className="h-4 w-4 text-gold" /><span className="eyebrow">Address</span></div>
              <a
                href={mapsUrl(`${venue.name} ${venue.addressLocality} CA`)}
                target="_blank" rel="noopener noreferrer nofollow"
                className="text-muted-foreground hover:text-gold inline-flex items-center gap-1"
              >
                {venue.addressStreet}, {venue.addressLocality} <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
            </div>
            <div className="border border-border bg-background p-5">
              <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-gold" /><span className="eyebrow">Category</span></div>
              <p className="text-muted-foreground">{venue.category}</p>
            </div>
            <div className="border border-border bg-background p-5">
              <div className="flex items-center gap-2 mb-1"><Plane className="h-4 w-4 text-gold" /><span className="eyebrow">Nearest airport</span></div>
              <p className="text-muted-foreground">SJC · {venue.drive.sjc}</p>
            </div>
            <div className="border border-border bg-background p-5">
              <div className="flex items-center gap-2 mb-1"><Trophy className="h-4 w-4 text-gold" /><span className="eyebrow">Primary use</span></div>
              <p className="text-muted-foreground text-xs leading-relaxed">{venue.primaryUse}</p>
            </div>
          </div>
        </div>
      </section>

      {/* AIRPORT DRIVE TIMES */}
      <section className="border-t border-border">
        <div className="container-x py-16">
          <div className="flex items-center gap-3 mb-8">
            <Plane className="h-5 w-5 text-gold" />
            <h2 className="text-2xl">Airport drive times to {venue.shortName}</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { code: "SFO", name: "San Francisco Int'l", time: venue.drive.sfo, slug: "sfo" },
              { code: "SJC", name: "San José Mineta", time: venue.drive.sjc, slug: "sjc" },
              { code: "OAK", name: "Oakland Int'l", time: venue.drive.oak, slug: "oak" },
            ].map((a) => (
              <Link
                key={a.code}
                to="/airports/$code"
                params={{ code: a.slug }}
                className="border border-border bg-background p-6 hover:border-gold transition-colors block"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-display text-3xl text-gold">{a.code}</span>
                  <span className="text-sm text-muted-foreground">{a.time}</span>
                </div>
                <p className="text-sm text-muted-foreground">{a.name}</p>
              </Link>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Typical drive times outside peak traffic. Every ride is flight-tracked and pre-arranged.
            Also serving{" "}
            <Link to="/airports" className="text-gold underline underline-offset-2">Monterey Regional (MRY) and Bay Area FBOs</Link>.
          </p>
        </div>
      </section>

      {/* EVENTS / USE CASES */}
      <section className="border-t border-border bg-surface">
        <div className="container-x py-16">
          <div className="flex items-center gap-3 mb-8">
            <Calendar className="h-5 w-5 text-gold" />
            <h2 className="text-2xl">Events & use cases we handle at {venue.shortName}</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {venue.events.map((ev) => (
              <div key={ev} className="border border-border bg-background p-4 text-sm text-muted-foreground">
                {ev}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VEHICLE RECOMMENDATIONS */}
      <section className="border-t border-border">
        <div className="container-x py-16">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
            <div>
              <p className="eyebrow mb-2">Recommended vehicles</p>
              <h2 className="text-2xl">The right car for your {venue.shortName} trip</h2>
            </div>
            <Link to="/fleet" className="text-sm text-gold underline underline-offset-2">View full fleet →</Link>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {venue.vehicles.map((veh) => (
              <Link
                key={veh.name}
                to="/fleet"
                className="border border-border bg-background p-5 hover:border-gold transition-colors block"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Car className="h-4 w-4 text-gold" />
                  <span className="eyebrow">Recommended</span>
                </div>
                <p className="font-display text-base mb-1">{veh.name}</p>
                <p className="text-xs text-muted-foreground">{veh.use}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PICKUP / DROP-OFF LOGISTICS */}
      <section className="border-t border-border bg-surface">
        <div className="container-x py-16 max-w-3xl">
          <p className="eyebrow mb-4">Pickup & drop-off logistics</p>
          <h2 className="text-3xl mb-5">How pickup and drop-off work at {venue.shortName}.</h2>
          <p className="text-muted-foreground leading-relaxed">{venue.logistics}</p>
        </div>
      </section>

      {/* NEARBY HOTELS */}
      <section className="border-t border-border">
        <div className="container-x py-16">
          <div className="flex items-center gap-3 mb-8">
            <Building2 className="h-5 w-5 text-gold" />
            <h2 className="text-2xl">Hotels near {venue.shortName}</h2>
          </div>
          <ul className="grid gap-2 md:grid-cols-2 text-sm">
            {venue.hotels.map((h) => (
              <li key={h} className="border-b border-border pb-2">
                <a
                  href={mapsUrl(`${h.replace(/\s*\([^)]*\)\s*$/, "")} ${venue.city} CA`)}
                  target="_blank" rel="noopener noreferrer nofollow"
                  className="text-muted-foreground hover:text-gold inline-flex items-center gap-1"
                >
                  {h}
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FLEET IMAGE */}
      <section className="border-t border-border">
        <div className="container-x py-12 max-w-5xl">
          <figure className="border border-border overflow-hidden bg-surface">
            <img
              src={fleetHero}
              alt={fleetAlt}
              width={1600}
              height={912}
              loading="lazy"
              decoding="async"
              className="w-full h-auto block"
            />
            <figcaption className="sr-only">{fleetAlt}</figcaption>
          </figure>
        </div>
      </section>

      {/* SEO / AEO copy */}
      <section className="border-t border-border bg-surface">
        <div className="container-x py-16 max-w-3xl">
          <p className="eyebrow mb-4">Why {venue.shortName} guests choose us</p>
          <h2 className="text-3xl mb-5">
            {venue.shortName} {venue.h1Suffix} — flight-tracked, CPUC-licensed & insured.
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Premier Executive Chauffeur — a division of{" "}
              <a href={SITE.parentUrl} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2">{SITE.parent}</a>{" "}
              — has been moving executives, event guests, corporate groups, and VIPs to {venue.name} for years. In {venue.city}, we're the trusted choice of executive assistants, event planners, and travel managers who need a chauffeur who's there on time, every time.
            </p>
            <p>
              Every {venue.shortName} chauffeur assignment is dispatched with flight tracking, pre-arranged pricing, and a fully insured, TCP-permitted vehicle regulated by the {SITE.regulator}. Our chauffeurs pass third-party background checks and are enrolled in a random drug testing program.
            </p>
            <p>
              Whether you're heading to a{" "}
              {venue.category === "Stadium" || venue.category === "Arena" ? "game, concert, or corporate suite event" :
               venue.category === "Convention Center" ? "keynote, product launch, or multi-day summit" :
               "board meeting, analyst day, or candidate on-site"}
              , we build the itinerary around your schedule. Book instantly on{" "}
              <a href={SITE.bookUrl} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2">our reservation page</a>{" "}
              or set up a{" "}
              <a href={SITE.corporateUrl} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2">corporate account</a>{" "}
              for consolidated billing.
            </p>
          </div>

          {cityData && (
            <div className="mt-10 border border-border p-6 bg-background">
              <p className="eyebrow mb-3">Also serving {cityData.name}</p>
              <p className="text-sm text-muted-foreground mb-3">{cityData.intro}</p>
              <Link
                to="/service-areas/$city"
                params={{ city: cityData.slug }}
                className="text-sm text-gold underline underline-offset-2"
              >
                View all {cityData.name} chauffeur services →
              </Link>
            </div>
          )}
        </div>
      </section>

      <ServiceStandards
        heading={`The standard we hold on every ${venue.shortName} run`}
        intro={`What is included on each ${venue.name} assignment — flat pricing approved before the ride, live flight tracking on airport legs, CPUC-licensed vehicles, and dispatch on call around the clock.`}
      />

      <HourlyRates
        layout="table"
        heading={`Hourly as-directed rates for ${venue.shortName}`}
        intro={`Published hourly pricing by vehicle class for ${venue.name} — event days, corporate campus visits, and wait-and-return itineraries. Five-hour minimum on hourly bookings; one-way transfers to and from ${venue.shortName} are quoted as flat all-in rates instead.`}
      />

      <InstantPriceWidget defaultPickup={venue.name} />

      <Testimonials
        heading={`What ${venue.shortName} guests say`}
        intro={`Verified Google reviews from executives, event guests, and families we've driven to and from ${venue.city} and the wider Bay Area.`}
      />

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="container-x py-20 max-w-3xl speakable">
          <p className="eyebrow mb-4">FAQ</p>
          <h2 className="text-3xl mb-8">
            {venue.shortName} chauffeur service — frequently asked questions
          </h2>
          <div className="divide-y divide-border">
            {venue.faqs.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none flex justify-between items-start gap-4 text-lg hover:text-gold">
                  <span>{f.q}</span>
                  <span className="text-gold text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED VENUES */}
      {related.length > 0 && (
        <section className="border-t border-border bg-surface">
          <div className="container-x py-16">
            <p className="eyebrow mb-4">Related venues & campuses</p>
            <h2 className="text-2xl mb-8">Also served by our chauffeurs</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/venues/$slug"
                  params={{ slug: r.slug }}
                  className="border border-border bg-background p-5 hover:border-gold transition-colors block group"
                >
                  <p className="eyebrow mb-2">{r.category}</p>
                  <p className="font-display text-base mb-1 group-hover:text-gold">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.city}, CA</p>
                </Link>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-6">
              Or browse{" "}
              <Link to="/venues" className="text-gold underline underline-offset-2">all Bay Area venues & campuses we serve</Link>.
            </p>
          </div>
        </section>
      )}

      <CTABand
        heading={`Reserve your ${venue.shortName} chauffeur`}
        sub={`SFO in ${venue.drive.sfo}. SJC in ${venue.drive.sjc}. OAK in ${venue.drive.oak}. Book online or call ${SITE.phone}.`}
      />
    </>
  );
}
